// Hand-written types for the foundational scaffold. Once the server exposes
// /v1/openapi.json, replace these with generated types (npm run gen:api → api-types.ts).

import type { CellChange, CellScope, GridStructureChange, PartKind } from './grid';

export type { CellChange, CellScope, GridStructureChange, PartKind };

export interface Me {
  userId: string;
  name: string;
  /** Personal name alone (Graph `/me` `givenName`) — what the top bar greets you by. */
  givenName: string;
  /** Corporate address, lowercased. The key a bid roster is stored under. */
  email: string;
  /** TENANT-level roles (corporate-KB ACL trimming). NOT the per-bid permission axis. */
  roles: string[];
  bidIds: string[];
  /** What the caller may do INSIDE each bid they belong to. The key set is `bidIds`. */
  memberships: Membership[];
  /** Beta features are enabled for this account (backend `BETA_TESTERS`). Optional because a
   *  backend older than the flag omits it — read through lib/beta.ts#useBetaTester, which treats
   *  its absence as "not a tester". */
  betaTester?: boolean;
  /** May create bids — a bid manager ("Niobe" Entra group) or a tenant admin. Gates
   *  "New bid"; the server enforces it on POST /v1/bids regardless. Optional for the same reason as
   *  `betaTester`: absent reads as "may not". */
  canCreateBids?: boolean;
  /** The hidden template library's bid id — set for a tenant admin, `null` for everyone else
   *  (AB#269). Never in a bid list; the account menu's "Template library" opens it. Optional: an
   *  older backend omits it, which reads as "no library". */
  templateLibraryBidId?: string | null;
}

/** What a member does on a deal. Descriptive metadata — it grants NOTHING. */
export type BidRole = 'BidManager' | 'SolutionLead' | 'DealAnalyst' | 'Contributor';

/** The permission axis, and the only one: `admin` ⊃ `editor` ⊃ `viewer`. */
export type Profile = 'admin' | 'editor' | 'viewer';

export const BID_ROLES: BidRole[] = ['BidManager', 'SolutionLead', 'DealAnalyst', 'Contributor'];
export const PROFILES: Profile[] = ['admin', 'editor', 'viewer'];

/** The caller's own membership of one bid, as GET /v1/me returns it. */
/**
 * The caller's own settings — GET/PATCH /v1/me/settings (AB#388): the preferences that follow a
 * member across browsers and machines. A flat allow-listed map (backend/domain/user-settings.ts):
 * only `lang` (en | fr) today; a key never written is absent.
 */
export type UserSettings = { lang?: 'en' | 'fr' } & Record<string, string | undefined>;

export interface Membership {
  bidId: string;
  role: BidRole;
  profile: Profile;
}

/** A row of a bid's roster, as GET /v1/bids/:bid/members returns it (and GET /v1/bids, per bid). */
export interface BidMember {
  bidId: string;
  /** Lowercased corporate address — the identity. The display name is display only. */
  email: string;
  /** Entra oid, bound at their first sign-in. `null` = invited, never signed in. */
  userId: string | null;
  displayName: string;
  role: BidRole;
  profile: Profile;
  addedBy: string;
  createdAt: string;
}

/** One colleague from the corporate directory lookup (GET /v1/directory/search). */
export interface DirectoryPerson {
  email: string;
  displayName: string;
  givenName?: string;
}

export interface ApiError {
  error: { code: string; message: string };
}

export type RevisionStatus = 'working' | 'applied' | 'failed';

/**
 * Which side of the member holds a lock / made a change. DISPLAY ONLY: the console agent
 * authenticates AS the member, so `actor` is identical either way — this exists purely so the IHM
 * can say "Sofia's agent is editing §3" instead of "Sofia is editing §3".
 *
 * Parity: backend/domain/revisions.ts#ActorKind must match.
 */
export type ActorKind = 'human' | 'agent';

/**
 * An in-flight AI edit — and the § lock itself: it lives in the room's Y.Map('revisions'),
 * written on open and deleted on apply/fail. Hand-parity with backend/domain/revisions.ts.
 */
export interface Revision {
  id: string;
  room: string;
  /** Who holds the lock (userId) — the only field authorization compares against. */
  actor: string;
  /** Display name of the holder, for the lockbar/chip. Never used for authorization. */
  actorName?: string;
  /** Whether a human (a browser) or the member's console agent holds this. Absent = 'human':
   *  records written before the field existed are still live in persisted ydocs. */
  actorKind?: ActorKind;
  prompt: string;
  status: RevisionStatus;
  suggestion?: string;
  /** Outline number of the locked § ("3.2.4"). '' or absent = the whole part. */
  section?: string;
  /** The § heading text captured at claim time — the drift/vanish detector on apply. */
  sectionTitle?: string;
  /** GRID rooms: the cell range this claim covers, by STABLE IDS (rows move, so a rectangle would
   *  drift). ADVISORY — two cell claims coexist, because Yjs already resolves two writes to one
   *  cell. An unscoped claim on a grid is a STRUCTURAL op and does freeze it. */
  cells?: CellScope;
  /** SLIDES rooms: the one slide this claim holds (`read_slide`). Absent on a slides room = the
   *  whole part (insert/delete/move slides). The editor shows a claimed slide read-only. */
  slide?: string;
  /** ISO instant the lock lapses. Absent (legacy record) = treated as already expired. */
  expiresAt?: string;
  createdAt: string;
}

export type HistoryKind = 'ai' | 'human';

/**
 * One modification recorded in the room's history (lives in the ydoc's Y.Array('history')).
 * Stores only the changed window {start, before, after} — never the whole document — plus
 * a little frozen context, so the UI renders the inline diff without recomputing.
 */
export interface HistoryEntry {
  id: string;
  /** Who made the change (userId). */
  actor: string;
  /** Display name of the author, so the log stays readable without a directory lookup. */
  actorName?: string;
  /** Human (a browser) or the member's console agent. Absent = 'human': entries written before
   *  the field existed are still live in persisted ydocs. Display only, like actorName — rendered
   *  through lib/actor-name.ts#actorDisplayName, the same helper the lock bar uses.
   *  Parity: backend/domain/history.ts#HistoryEntry must match. */
  actorKind?: ActorKind;
  kind: HistoryKind;
  prompt?: string;
  /** MARKDOWN parts only — offsets into the part's text. A grid entry carries `cells`/`structure`
   *  instead, and a reader tells the two apart with `isGridEntry` (never a discriminant field:
   *  entries written before grids existed have none). */
  start?: number;
  before?: string;
  after?: string;
  contextBefore?: string;
  contextAfter?: string;
  /** Outline number of the § the edit was scoped to ('' / absent = the whole part).
   *  Display only, and never a jump target — numbers drift as headings are added. */
  section?: string;
  /** The § heading text at write time, so the label stays readable after a renumber. */
  sectionTitle?: string;
  /** GRID parts: the cells this edit changed, labels frozen at write time. */
  cells?: CellChange[];
  /** GRID parts: the structural ops it performed, grouped by kind. */
  structure?: GridStructureChange[];
  /** GRID parts: `cells` was capped — the write was larger than the log records. */
  truncated?: boolean;
  /** SLIDES parts: per slide the batch touched, a short label of what changed. */
  slides?: Array<{ slide: string; label: string }>;
  /** A server-side restore (POST /v1/restore): the version it brought back. A 'human' entry. */
  restoredFrom?: number;
  /** A manual flatten (POST /v1/versions/flatten): how many versions it removed. A 'human' entry
   *  whose `prompt` reads "History flattened — N versions removed". */
  flattened?: number;
  /** A bid Office template's draft: the settings this write changed (backend
   *  domain/office-template-draft.ts#ThemeChange; `before`/`after` are field values). */
  theme?: Array<{ field: string; before: unknown; after: unknown }>;
  /** A template draft's publish: the version it made. */
  published?: number;
  /** A template draft's discard: how many unpublished changes it dropped. */
  discarded?: number;
  createdAt: string;
}

/** A member's tag on a version (AB#239): free text since AB#241. Parity:
 *  backend/domain/versions.ts#VersionTag. */
export type VersionTag = string;
/** The tags offered as suggestions (each has a coloured badge); any other text is a tag too.
 *  Parity: backend/domain/versions.ts#SUGGESTED_VERSION_TAGS. */
export const SUGGESTED_VERSION_TAGS = ['bronze', 'silver', 'gold'] as const;
export type SuggestedVersionTag = (typeof SUGGESTED_VERSION_TAGS)[number];
/** Longest tag a version takes. Parity: backend/domain/versions.ts#VERSION_TAG_MAX. */
export const VERSION_TAG_MAX = 40;
/** Longest comment a version takes. Parity: backend/domain/versions.ts#VERSION_COMMENT_MAX. */
export const VERSION_COMMENT_MAX = 1000;

/**
 * A restore point of a room, without its snapshot body (GET /v1/versions). Parity:
 * backend/domain/versions.ts#VersionMeta.
 */
export interface VersionMeta {
  version: number;
  label: string;
  kind: string;
  format?: 'md' | 'grid' | 'slides';
  /** The history entry this restore point belongs to; absent on every untagged version. */
  historyId?: string;
  /** Who made the change (frozen from the history entry when there is one). */
  author: string;
  createdAt: string;
  /** Frozen from the history entry: an AI or a human change, and a one-line description. */
  actorKind?: 'ai' | 'human';
  summary?: string;
  tag?: VersionTag;
  comment?: string;
  taggedBy?: string;
  taggedAt?: string;
}

/** What a flatten removes (or, dry run, would remove): POST /v1/versions/flatten (AB#242). */
export interface FlattenCounts {
  /** Versions older than the kept one, deleted for good. */
  removed: number;
  /** Older versions kept because they are tagged or named. */
  keptProtected: number;
}

/** What a version changed since the previous surviving one (GET /v1/versions/:version/diff). */
export interface VersionDiff {
  version: number;
  previous: number | null;
  /** How many removed versions this diff spans (0 when adjacent). */
  merged: number;
  change:
    | { format: 'md'; start: number; before: string; after: string }
    | { format: 'grid'; structure: GridStructureChange[]; cells: CellChange[]; truncated?: boolean }
    | { format: 'slides'; slides: Array<{ slide: string; label: string }> };
}

/**
 * Is this a grid entry? Asked of the PAYLOAD, not of a discriminant — see HistoryEntry above.
 * An entry with neither shape is a markdown one (every entry written before grids existed).
 */
export function isGridEntry(entry: HistoryEntry): boolean {
  return Boolean(entry.cells?.length || entry.structure?.length);
}
